import { useState } from 'react';
import { Button, Divider, Group, Text as MantineText, Popover, Stack, TextInput, Tooltip } from '@mantine/core';
import { IconFlag, IconWorld } from '@tabler/icons-react';
import { flagAssetUrl, toFlagCountryCode } from '@utils/country-flag';
import useReportCompanyLogo from '../../app/(dashboard)/doc-pabean/apis/use-cases/use-report-company-logo';

/**
 * Legal-form and honorific prefixes stripped before deriving initials. These
 * mirror the backend normalizer (domain/company_enrichment/models/normalize.go)
 * so a company reads the same on both sides: "PT Sarana Jaya" must become "SJ",
 * not "PS".
 */
const LEGAL_TOKENS = new Set(['pt', 'cv', 'firma', 'fa', 'tbk', 'persero', 'ptt', 'ms']);

/** Words that carry no identity, so they never contribute an initial. */
const GENERIC_TOKENS = new Set([
  'indonesia',
  'international',
  'manufacturing',
  'marketing',
  'asia',
  'pacific',
  'global',
  'limited',
  'private',
  'pte',
  'ltd',
  'co',
  'inc',
  'corp',
  'corporation',
  'company',
  'group',
  'industries',
  'industry',
]);

/**
 * companyInitials derives up to two letters from a company name.
 *
 * Legal forms and geography are dropped first: without that, half the shipper
 * column would read "PI" (PT ... Indonesia) and the badges would be useless as
 * a way to tell rows apart.
 */
export function companyInitials(name: string | null | undefined): string {
  if (!name) return '?';

  const tokens = name
    .toLowerCase()
    .replace(/[^a-z0-9\s&]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .filter((t) => !LEGAL_TOKENS.has(t));

  // Prefer distinctive words, but never return empty: a company literally
  // named "PT Indonesia" still needs a badge.
  const distinctive = tokens.filter((t) => !GENERIC_TOKENS.has(t));
  const source = distinctive.length > 0 ? distinctive : tokens;
  if (source.length === 0) return '?';

  if (source.length === 1) {
    return source[0].slice(0, 2).toUpperCase();
  }
  return (source[0][0] + source[1][0]).toUpperCase();
}

/**
 * badgeColor picks a stable colour per company so the same company always
 * looks the same, and neighbouring rows are easy to tell apart. Derived from
 * the name, not random, so it survives re-renders and pagination.
 */
function badgeColor(name: string): { bg: string; fg: string } {
  const palette = [
    { bg: '#E3F2FD', fg: '#1565C0' },
    { bg: '#F3E5F5', fg: '#6A1B9A' },
    { bg: '#E8F5E9', fg: '#2E7D32' },
    { bg: '#FFF3E0', fg: '#E65100' },
    { bg: '#E0F7FA', fg: '#00695C' },
    { bg: '#FCE4EC', fg: '#AD1457' },
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return palette[Math.abs(hash) % palette.length];
}

interface CompanyNameCellProps {
  name: string | null | undefined;
  logoUrl?: string | null;
  /**
   * True when the stored artwork is a site's "negative" variant — white, and
   * therefore invisible against this table's light row. Such logos render on a
   * dark chip instead of a transparent slot. The backend decides this from the
   * pixels; 13 of the first 90 enriched companies were in this state.
   */
  logoOnDark?: boolean | null;
  /** Resolved company website, shown so the user can check it. */
  website?: string | null;
  /**
   * Raw shipper/consignee country, in whatever shape the filing carried it —
   * an alpha-2 code, an alpha-3 code, or a full name. Passed only by the
   * doc-pabean master table, where the Country column was folded into this cell;
   * every other caller leaves it undefined and renders exactly as before.
   */
  country?: string | null;
  /** Only the tokens this cell actually reads, rather than the whole bag. */
  themeColors: { tableText: string; text?: string; textSecondary?: string; card?: string; border?: string };
}

/** Strips the scheme so the tooltip shows a domain, not a URL. */
function displayDomain(website: string): string {
  return website
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '');
}

/**
 * CompanyNameCell renders a company's logo beside its name in the document
 * table.
 *
 * The logo sits in a fixed-size "well" with object-fit: contain, so every row
 * has an identically sized slot whatever shape the logo is. That matters
 * because real logos vary enormously: square favicons sit centred, while wide
 * wordmarks (MAHLE publishes a 113x25 mark) letterbox to fit. A circular
 * avatar would squash the latter to an illegible sliver.
 *
 * Letterboxing at render time rather than baking it into the stored image
 * keeps the asset pristine and makes the slot size a CSS decision.
 */
export function CompanyNameCell({ name, logoUrl, logoOnDark, website, country, themeColors }: CompanyNameCellProps) {
  // A stored logo_url can still 404 — the source site may have moved it since
  // enrichment ran — so a load failure has to degrade to initials at runtime,
  // not just when the URL is absent.
  const [failed, setFailed] = useState(false);
  // A vendored flag can be absent for an obscure ISO code. Degrade to the bare
  // country code rather than a broken-image glyph, the same way the logo does.
  const [flagFailed, setFlagFailed] = useState(false);
  const [opened, setOpened] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const { report, loading } = useReportCompanyLogo();

  const label = name?.trim() || '-';
  const showLogo = Boolean(logoUrl) && !failed;
  // Only ever applied behind a logo that is actually showing: a dark chip
  // behind initials would read as a different kind of badge entirely.
  const onDarkChip = showLogo && Boolean(logoOnDark);
  const iso2 = toFlagCountryCode(country);
  const flagSrc = flagAssetUrl(iso2);
  const colors = badgeColor(label);
  const hasCompany = label !== '-';

  const send = async (kind: 'WRONG_LOGO' | 'SUGGEST_WEBSITE' | 'MISSING_LOGO') => {
    const ok = await report({
      companyName: label,
      kind,
      suggestedWebsite: kind === 'SUGGEST_WEBSITE' ? suggestion.trim() : undefined,
      observedLogoUrl: logoUrl ?? null,
      observedWebsite: website ?? null,
    });
    if (ok) {
      setOpened(false);
      setSuggestion('');
    }
  };

  const well = (
    <div
      style={{
        width: 56,
        height: 22,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 4,
        overflow: 'hidden',
        cursor: hasCompany ? 'pointer' : 'default',
        // Three states, not two: initials keep their tinted badge, a normal
        // logo sits on nothing at all, and a white logo gets a dark chip so it
        // has something to be seen against.
        backgroundColor: onDarkChip ? '#1f2937' : showLogo ? 'transparent' : colors.bg,
        // The chip needs breathing room or the artwork touches its edges and
        // reads as a cropped tile rather than a logo on a background.
        padding: onDarkChip ? '2px 4px' : undefined,
      }}
    >
      {showLogo ? (
        <img
          src={logoUrl as string}
          alt=""
          onError={() => setFailed(true)}
          loading="lazy"
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      ) : (
        <MantineText size="10px" fw={700} style={{ color: colors.fg, letterSpacing: 0.3 }}>
          {companyInitials(label)}
        </MantineText>
      )}
    </div>
  );

  return (
    <Group gap={8} wrap="nowrap" justify="flex-start" style={{ width: '100%' }}>
      {hasCompany ? (
        <Popover
          opened={opened}
          onChange={setOpened}
          position="bottom-start"
          shadow="md"
          width={280}
          withinPortal
          trapFocus
        >
          <Popover.Target>
            <div
              role="button"
              tabIndex={0}
              aria-label={`Logo ${label}`}
              onClick={() => setOpened((o) => !o)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOpened((o) => !o);
                }
              }}
              style={{ display: 'flex' }}
            >
              {well}
            </div>
          </Popover.Target>

          <Popover.Dropdown p="sm">
            <Stack gap={8}>
              <MantineText size="xs" fw={600} style={{ color: themeColors.text }}>
                {label}
              </MantineText>

              {/* The domain, not a confidence score. It is the fact a user can
                  actually check — seeing the wrong company's site here is what
                  makes a wrong logo obvious. */}
              <Group gap={6} wrap="nowrap">
                <IconWorld size={13} style={{ color: themeColors.textSecondary, flexShrink: 0 }} />
                <MantineText size="xs" style={{ color: themeColors.textSecondary, wordBreak: 'break-all' }}>
                  {website ? displayDomain(website) : 'Belum ada website'}
                </MantineText>
              </Group>

              <Divider />

              {showLogo ? (
                <Button
                  size="compact-xs"
                  variant="light"
                  color="red"
                  leftSection={<IconFlag size={12} />}
                  loading={loading}
                  onClick={() => send('WRONG_LOGO')}
                >
                  Logo ini salah
                </Button>
              ) : (
                <Button size="compact-xs" variant="light" loading={loading} onClick={() => send('MISSING_LOGO')}>
                  Perusahaan ini punya logo
                </Button>
              )}

              {/* The high-value contribution: the website is the expensive half
                  of enrichment, so someone who knows their own trading partner
                  resolves in one step what search and guessing could not. */}
              <TextInput
                size="xs"
                placeholder="Website yang benar, mis. mahle.com"
                value={suggestion}
                onChange={(e) => setSuggestion(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && suggestion.trim()) send('SUGGEST_WEBSITE');
                }}
              />
              <Button
                size="compact-xs"
                variant="filled"
                disabled={!suggestion.trim()}
                loading={loading}
                onClick={() => send('SUGGEST_WEBSITE')}
              >
                Kirim website
              </Button>

              <MantineText size="10px" style={{ color: themeColors.textSecondary }}>
                Laporan ditinjau dulu sebelum diterapkan.
              </MantineText>
            </Stack>
          </Popover.Dropdown>
        </Popover>
      ) : (
        well
      )}

      <MantineText
        size="xs"
        c={themeColors.tableText}
        style={{
          flex: 1,
          minWidth: 0,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          lineHeight: 1.3,
        }}
      >
        {label}
      </MantineText>

      {/* Country of the shipper, folded in from the removed Country column: the
          flag carries it at a glance, the code keeps it exact. Sits at the end
          and never shrinks, so the name absorbs any squeeze by wrapping. */}
      {iso2 && (
        <Tooltip label={country?.trim() || iso2} withArrow position="top">
          <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
            {flagSrc && !flagFailed && (
              <img
                src={flagSrc}
                alt=""
                onError={() => setFlagFailed(true)}
                loading="lazy"
                style={{
                  width: 16,
                  height: 12,
                  objectFit: 'cover',
                  borderRadius: 2,
                  // A hairline so white-field flags (JP, KR) still read as a flag
                  // and not as a gap in the row.
                  boxShadow: `inset 0 0 0 1px ${themeColors.border ?? 'rgba(0,0,0,0.15)'}`,
                  display: 'block',
                }}
              />
            )}
            <MantineText
              size="10px"
              fw={600}
              style={{ color: themeColors.textSecondary ?? themeColors.tableText, letterSpacing: 0.3 }}
            >
              {iso2}
            </MantineText>
          </Group>
        </Tooltip>
      )}
    </Group>
  );
}
